import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { CtaSection } from "@/components/CtaSection";
import { ResultsGallerySection } from "@/components/ResultsGallery";
import { SERVICES } from "@/lib/content";
import { useSeo } from "@/hooks/useSeo";

const blocks = SERVICES.filter((s) => ["end-of-lease", "medical", "education", "industrial", "specialised"].includes(s.id));

const notes = {
  "end-of-lease": "Moving out? We prepare rental properties for inspection with detailed kitchen, bathroom and interior cleaning — so the property is presented at its best.",
  medical: "Hygiene-focused cleaning with careful attention to high-touch surfaces and sanitisation — designed for clinics, dental practices and healthcare environments.",
  education: "Careful, consistent cleaning for childcare centres and schools, with attention to classrooms, common areas, bathrooms and high-touch surfaces.",
  industrial: "Heavy-duty cleaning for warehouses, factories and workshops — floors, high-traffic areas, staff facilities and industrial common areas.",
  specialised: "Deep cleans, sanitisation, event preparation and post-renovation cleaning — tailored solutions for requirements outside the everyday.",
};

export default function Specialised() {
  useSeo("Specialised Cleaning Perth | End of Lease, Medical, Industrial | RD Cleaning Services", "End of lease cleaning, medical & healthcare cleaning, childcare cleaning, warehouse and industrial cleaning across Perth, WA. Detail-focused specialised cleaning services.");
  return (
    <>
      <PageHero
        eyebrow="Specialised Cleaning Perth"
        title="Specialised Cleaning, Done Properly"
        subtitle="End of lease, medical & healthcare, education & childcare, industrial and custom cleaning — where attention to detail matters most."
        image={blocks[1].image}
      >
        <Link to="/quote" className="focus-ring inline-flex items-center gap-2 rounded-full bg-teal-500 px-7 py-3.5 text-sm font-bold text-navy-deep transition-all hover:-translate-y-0.5 hover:bg-teal-400" data-testid="specialised-hero-quote-button">
          Get a Free Quote <ArrowRight className="h-4 w-4" />
        </Link>
      </PageHero>

      <section className="bg-white py-20 sm:py-28">
        <div className="mx-auto max-w-7xl space-y-20 px-4 sm:px-6 lg:px-8">
          {blocks.map((s, i) => (
            <Reveal key={s.id}>
              <article className="grid items-center gap-10 lg:grid-cols-2" data-testid={`specialised-block-${s.id}`}>
                <div className={i % 2 === 1 ? "lg:order-2" : ""}>
                  <div className="overflow-hidden rounded-3xl shadow-xl">
                    <img src={s.image} alt={`${s.name} in Perth`} loading="lazy" className="aspect-[16/10] w-full object-cover" />
                  </div>
                </div>
                <div className={i % 2 === 1 ? "lg:order-1" : ""}>
                  <span className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-teal-600">{String(i + 1).padStart(2, "0")}</span>
                  <h2 className="mt-3 font-display text-2xl font-extrabold tracking-tight text-navy sm:text-3xl">{s.name}</h2>
                  <p className="mt-4 leading-relaxed text-slate-600">{notes[s.id]}</p>
                  <ul className="mt-6 grid grid-cols-1 gap-x-6 gap-y-2.5 sm:grid-cols-2">
                    {s.items.map((item) => (
                      <li key={item} className="flex items-start gap-2 text-sm text-slate-700">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-teal-600" /> {item}
                      </li>
                    ))}
                  </ul>
                  <Link to="/quote" className="focus-ring mt-8 inline-flex items-center gap-2 rounded-full bg-teal-600 px-6 py-3 text-sm font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-teal-700" data-testid={`specialised-quote-${s.id}`}>
                    Get a Quote <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <ResultsGallerySection
        slugs={["medical-clinic", "childcare-centre", "post-construction", "warehouse"]}
        heading="Before & After in Specialised Environments"
        text="Clinics, childcare, industrial sites and post-construction hand-overs, shown side by side."
        bg="bg-slate-50"
        testid="specialised-gallery-section"
      />

      <CtaSection heading="Have a Specific Cleaning Requirement?" text="Tell us what you need — if it can be cleaned, we'll find a way to clean it properly." />
    </>
  );
}
